import {
  type PropertyVentilation as DbPropertyVentilation,
  VENTILATION_PLANS_ANSWERS,
  ventilationPlansDeliveryEnum,
  visitPreInfos,
} from '@markku/db'
import { createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

export const ventilationPlansSchema = z.enum(VENTILATION_PLANS_ANSWERS)
export type VentilationPlansAnswer = z.infer<typeof ventilationPlansSchema>

export const ventilationPlansDeliverySchema = z.enum(
  ventilationPlansDeliveryEnum.enumValues,
)
export type VentilationPlansDelivery = z.infer<
  typeof ventilationPlansDeliverySchema
>

export const selectVisitPreInfoSchema = createSelectSchema(visitPreInfos, {
  internalReviewedAt: z.string().nullable().optional(),
  internalReviewedBy: z.number().nullable().optional(),
  internalReviewComment: z.string().nullable().optional(),
})
export type VisitPreInfo = z.infer<typeof selectVisitPreInfoSchema>

export const MAX_VENTILATION_UNITS = 10

export const ventilationUnitSchema = z.object({
  brand: z.string().max(120).optional(),
  model: z.string().max(120).optional(),
  location: z.string().max(200).optional(),
})
export type VentilationUnit = z.infer<typeof ventilationUnitSchema>

/** The shape of `properties.ventilation`; kept in step with the db type. */
export const propertyVentilationSchema = z.object({
  units: z.array(ventilationUnitSchema).max(MAX_VENTILATION_UNITS),
  hasPlans: ventilationPlansSchema.nullish(),
  accessNotes: z.string().max(1000).nullish(),
  notes: z.string().max(2000).nullish(),
}) satisfies z.ZodType<DbPropertyVentilation>
export type PropertyVentilation = z.infer<typeof propertyVentilationSchema>

export const EMPTY_VENTILATION: PropertyVentilation = { units: [] }

export const preInfoAnswersSchema = z.object({
  units: z.array(ventilationUnitSchema).max(MAX_VENTILATION_UNITS),
  hasPlans: ventilationPlansSchema.optional(),
  plansDelivery: ventilationPlansDeliverySchema.optional(),
  pets: z.string().max(500).optional(),
  accessConfirmed: z.boolean().optional(),
  accessNotes: z.string().max(1000).optional(),
  notes: z.string().max(2000).optional(),
})
export type PreInfoAnswers = z.infer<typeof preInfoAnswersSchema>

export const sendPreInfoRequestSchema = z.object({
  visitId: z.number(),
})

export const markPreInfoReviewedSchema = z.object({
  visitId: z.number(),
  reviewComment: z.string().optional(),
})

export const updateVentilationProfileSchema = propertyVentilationSchema.extend({
  propertyId: z.number(),
  pets: z.string().max(500).nullish(),
})

export const VENTILATION_PLANS_LABELS: Record<VentilationPlansAnswer, string> =
  {
    yes: 'Kyllä',
    no: 'Ei',
    unknown: 'Ei tiedä',
  }

export const VENTILATION_PLANS_DELIVERY_LABELS: Record<
  VentilationPlansDelivery,
  string
> = {
  uploaded: 'Lähetti kuvat',
  onsite: 'Etsii käyntipäiväksi',
}

export const PRE_INFO_ATTACHMENT_TYPES = {
  PLAN: 'ventilation_plan',
  UNIT_PLATE: 'ventilation_unit_plate',
} as const

export type PreInfoAttachmentType =
  (typeof PRE_INFO_ATTACHMENT_TYPES)[keyof typeof PRE_INFO_ATTACHMENT_TYPES]
